
import React, { useState, useEffect } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'

import { INTEGRATION_STATUSES } from 'constants/statuses'
import { getIntegration, INTEGRATION_CATEGORY_LABELS } from 'constants/integrations'

import { useShopifyConnectionStatus } from 'hooks/api'
import { useCanAccess } from 'hooks'
import { useSnackbar } from 'contexts'
import { ROUTES } from 'routes'

import { Box, Stack, Typography, useTheme, Tooltip, Link as MuiLink } from '@mui/material'
import { ArrowUpRightIcon, ActivityIcon } from 'lucide-react'

import { Button, Breadcrumbs } from '@repo/ui'
import {
  EmptyState,
  InfoBlock,
  InfoRow,
  InfoItem,
  IntegrationLogo,
  StatusChip,
  SegmentedTabs,
  ConnectShopifyModal,
  DisconnectIntegrationModal,
  ReferrerBackLink,
} from 'components'

import { FieldMappingEditor } from '../FieldsPage/components/FieldMappingEditor'
import { NARROW_COLUMN_WIDTH } from 'config'



export const IntegrationPage = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  const { name } = useParams()
  const [searchParams] = useSearchParams()
  const { open: notification } = useSnackbar()

  const [openConnectIntegrationModal, setOpenConnectIntegrationModal] = useState(false)
  const [openDisconnectIntegrationModal, setOpenDisconnectIntegrationModal] = useState(false)
  const [activeTab, setActiveTab] = useState(() => searchParams.get('tab') ?? 'field-mapping')

  const integration = getIntegration(name)
  const isShopify = integration?.key === 'shopify'
  const comingSoon = integration?.availability === 'coming-soon'

  // Viewing is open to any company user; connecting/disconnecting needs Manage store (VERA-405 §24/§27).
  const { isHasAccess: canManageStore } = useCanAccess({ action: 'manage_store' })

  // Use real Shopify connection status for Shopify integration
  const {
    data: shopifyStatus,
    isLoading: isLoadingShopifyStatus,
    refetch: refetchShopifyStatus
  } = useShopifyConnectionStatus()
  
  const connected = isShopify && shopifyStatus?.connected === true

  const isLoading = isShopify ? isLoadingShopifyStatus : false

  const handleRefetch = () => {
    if (isShopify) {
      refetchShopifyStatus()
    }
    // For other integrations, could add their refetch logic here
  }

  // Handle error parameters from URL (e.g., when redirected from backend with errors)
  useEffect(() => {
    const error = searchParams.get('error')
    if (error) {
      notification({
        type: 'error',
        title: 'Integration Error',
        subtitle: decodeURIComponent(error),
      })
      // Clear the error parameter from URL
      searchParams.delete('error')
      navigate(`${ROUTES.SETTINGS_COMPANY_INTEGRATION(name)}`, { replace: true })
    }
  }, [searchParams, notification, navigate, name])  

  if (!integration) {
    return (
      <EmptyState
        title="Integration not available"
        text="This integration isn't available yet."
      />
    )
  }

  return (
    // Capped to NARROW_COLUMN_WIDTH like its siblings InvoicePage / UserPage / the
    // Settings sections that opt in. This page is prose and a small card — at full
    // viewport width the Description ran to well over a hundred characters a line,
    // which is past the point where the eye loses its place returning to the left
    // margin, and left roughly a thousand pixels of dead space beneath it.
    //
    // `mx: 'auto'` matches SettingsPage's treatment rather than left-aligning: the
    // sidebar already anchors the left edge, so a left-aligned cap reads as a column
    // that failed to fill rather than a deliberate measure.
    //
    // RolePage is the documented exception (it needs the width for its permissions
    // matrix) — the cap is for reading surfaces, not for every settings page.
    <Stack sx={{ maxWidth: NARROW_COLUMN_WIDTH, mx: 'auto', width: '100%' }}>
      <Stack spacing={4}>
        <Stack spacing={2} pt={3} pb={2}>
          <Breadcrumbs />
        </Stack>

        <Stack direction="row" spacing={4} alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={4} alignItems="center">
            <IntegrationLogo src={integration.logo} alt={integration.name} />
            <Typography variant='h5'>{integration.name}</Typography>
            {/* Contextual back-link inline, right of the title — canonical
                placement matching the shared Header (was stacked above). */}
            <ReferrerBackLink />
            {isShopify && connected && (
              <StatusChip statuses={INTEGRATION_STATUSES} status="connected" />
            )}
          </Stack>

          <Stack direction="row" spacing={3} alignItems="center">
            {comingSoon ? (
              <Button disableElevation variant="secondary" disabled>
                Coming soon
              </Button>
            ) : connected ? (
              <Tooltip title={canManageStore ? '' : "You need the 'Manage store' permission to disconnect. Ask an admin to grant it."}>
                <span>
                  <Button
                    disableElevation
                    variant="secondary"
                    destructive="hover"
                    disabled={!canManageStore}
                    onClick={() => setOpenDisconnectIntegrationModal(true)}
                  >
                    Disconnect
                  </Button>
                </span>
              </Tooltip>
            ) : (
              <Tooltip title={canManageStore ? '' : "You need the 'Manage store' permission to connect. Ask an admin to grant it."}>
                <span>
                  <Button
                    disableElevation
                    variant="primary"
                    disabled={isLoading || !canManageStore}
                    onClick={() => setOpenConnectIntegrationModal(true)}
                  >
                    Connect
                  </Button>
                </span>
              </Tooltip>
            )}
          </Stack>
        </Stack>

        <InfoBlock>
          <InfoRow>
            <InfoItem label="Description" value={integration?.description} />
            <InfoItem label="Categories" value={integration?.categories?.map((category) => INTEGRATION_CATEGORY_LABELS[category]).join(', ')} />
          </InfoRow>
        </InfoBlock>

        {/* Connected: Field mapping (VERA-803) + Integration event log tabs */}
        {connected && (
          <Stack spacing={4} pt={2}>
            <SegmentedTabs
              tabs={[
                { label: 'Field mapping', value: 'field-mapping' },
                { label: 'Integration event log', value: 'event-log' },
              ]}
              value={activeTab}
              onChange={setActiveTab}
            />
            {activeTab === 'field-mapping' ? (
              <FieldMappingEditor
                provider={integration.key}
                providerLabel={integration.name}
              />
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, minHeight: 400 }}>
                <EmptyState
                  title="No events yet"
                  text={`Sync activity for your ${integration.name} connection will appear here.`}
                  icon={<ActivityIcon strokeWidth={1} size={32} />}
                />
              </Box>
            )}
          </Stack>
        )}

        {/* Disconnected: short generic reassurance + a link to the (versioned) setup docs (VERA-409) */}
        {!connected && !comingSoon && (
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
              You will be redirected to {integration.name} to authorize Vera. Connecting takes about a minute.
            </Typography>
            {integration.docsUrl && (
              <MuiLink
                href={integration.docsUrl}
                target="_blank"
                rel="noopener noreferrer"
                underline="hover"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1,
                  width: 'fit-content',
                  fontSize: 14,
                  color: theme.palette.brand[600],
                }}
              >
                View setup guide
                <ArrowUpRightIcon strokeWidth={1} size={16} />
              </MuiLink>
            )}
          </Stack>
        )}
      </Stack>

      {isShopify && (
        <ConnectShopifyModal
          integration={integration}
          open={openConnectIntegrationModal}
          onClose={() => setOpenConnectIntegrationModal(false)}
          refetch={handleRefetch}
        />
      )}
      
      <DisconnectIntegrationModal
        integration={integration}
        open={openDisconnectIntegrationModal}
        onClose={() => setOpenDisconnectIntegrationModal(false)}
        refetch={handleRefetch}
      />
    </Stack>
  )
}
